import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { ReactComponent as ContextMenuIcon } from '../assets/svg/icon-more-options.svg';

const GroupedDataTable = ({
  groupedData,
  headers,
  groupMeta = {},
  rowKeyFn,
  onPrimaryAction,
  contextualOptionsFn,
  faultToReportMap = {},
  t = (key) => key,
  getPrimaryActionLabel = (item) => t(`faultButtons.${item.status?.toLowerCase?.()}`),
  isFiltered = false, // ← ADD THIS LINE
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRefs = useRef({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      const isNowMobile = window.innerWidth <= 768;
      setIsMobile(isNowMobile);
      if (!isNowMobile) {
        setOpenMenuId(null);
        const allMobileDetails = document.querySelectorAll(".table-list-2__mobile-data-row");
        allMobileDetails.forEach((el) => (el.style.display = "none"));
        const selected = document.querySelectorAll(".table-list-2__selected-mobile");
        selected.forEach((row) => row.classList.remove("table-list-2__selected-mobile"));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    hasMountedRef.current = true;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideAny = Object.values(menuRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideAny) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const toggleMenu = (e, id) => {
    e.stopPropagation();
    setOpenMenuId((prevId) => (prevId === id ? null : id));
  };

  const getColorClass = (value, styleString) => {
    if (!value || !styleString) return "";
    const v = value.toLowerCase?.();
    const styles = styleString.split(",").map((s) => s.trim().toLowerCase());
    const classes = [];
    if (styles.includes("circle")) classes.push(`color-circle-${v}`);
    if (styles.includes("block")) classes.push(`color-block-${v}`);
    return classes.join(" ");
  };

  const groupEntries = Object.entries(groupedData).filter(
    ([_, entries]) => !isFiltered || entries.length > 0
  );

  if (groupEntries.length === 0) return null;

  const [firstGroup, ...otherGroups] = groupEntries;

  return (
    <>
      <div className="table-list-2__section-header">
        {groupMeta[firstGroup[0]]?.title || firstGroup[0]}
        {groupMeta[firstGroup[0]]?.description && (
          <div className="content__section-title-description">
            {groupMeta[firstGroup[0]].description}
          </div>
        )}
      </div>

      <table className="table-list-2">
        <thead>
          <tr>
            {headers.map((col, idx) => (
              <th
                key={idx}
                className={clsx({ "table-list-2__hide-mobile": col.hideMobile })}
              >
                {col.label}
              </th>
            ))}
            {contextualOptionsFn && <th className="table-list-2__hide-mobile"></th>}
          </tr>
        </thead>
        <tbody>
          {[firstGroup, ...otherGroups].map(([group, entries], groupIdx) => (
            <React.Fragment key={groupIdx}>
              {groupIdx > 0 && (
                <tr
                  className={`table-list-2__section-header${
                    entries.length === 0 ? ' table-list-2__section-header-no-items' : ''
                  }`}
                >
                  <td colSpan={headers.length + (contextualOptionsFn ? 1 : 0)}>
                    {groupMeta[group]?.title || group}
                    {groupMeta[group]?.description && (
                      <div className="content__section-title-description">
                        {groupMeta[group].description}
                      </div>
                    )}
                  </td>
                </tr>
              )}
              {/*
              {entries.length === 0 && (
                <tr>
                  <td colSpan={headers.length + (contextualOptionsFn ? 1 : 0)}>
                    <div className="content__section-title-description">
                      {groupMeta[group]?.emptyMessage}
                    </div>
                  </td>
                </tr>
              )}
              */}
              {entries.length === 0 ? (
                <tr className="table-list-2__no-items-rows">
                  <td colSpan={headers.length + (contextualOptionsFn ? 1 : 0)}>
                    {groupMeta[group]?.emptyMessage}
                  </td>
                </tr>
              ) : (
                entries.map((item) => {
                  const rowId = rowKeyFn(item);
                  const options = contextualOptionsFn ? contextualOptionsFn(item) : null;

                  const handleRowClick = (e) => {
                    const row = e.currentTarget;
                    const next = row.nextElementSibling;

                    if (isMobile && hasMountedRef.current && next) {
                      const isVisible = next.style.display === "table-row";
                      next.style.display = isVisible ? "none" : "table-row";
                      row.classList.toggle("table-list-2__selected-mobile", !isVisible);
                    } else if (onPrimaryAction) {
                      onPrimaryAction(item);
                    }
                  };

                  return (
                    <React.Fragment key={rowId}>
                      <tr
                        className="table-list-2__hoverable-row"
                        onClick={handleRowClick}
                      >
                        {headers.map((col, colIdx) => {
                          const value = col.render ? col.render(item) : item[col.accessor];
                          const colorValue = col.colorValueFrom ? col.colorValueFrom(item) : value;
                          const colorClass = col.colorStyle ? getColorClass(colorValue, col.colorStyle) : "";

                          return (
                            <td
                              key={colIdx}
                              className={clsx({
                                "table-list-2__hide-mobile": col.hideMobile,
                                "table-list-2__elastic-cell": col.elasticCell,
                                [colorClass]: !!colorClass,
                              })}
                            >
                              {colIdx === 0 && onPrimaryAction && (
                                <a
                                  className="table-list-2__primary-btn table-list-2__show-on-hover"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onPrimaryAction(item);
                                  }}
                                >
                                  {getPrimaryActionLabel(item)}
                                </a>
                              )}
                              {value}
                            </td>
                          );
                        })}
                        {options && (
                          <td
                            className="table-list-2__hide-mobile table-list-2__options table-list-2__align-right"
                            onClick={(e) => toggleMenu(e, rowId)}
                          >
                            <div className="table-list-2__options-icon">
                              <ContextMenuIcon />
                            </div>
                            {openMenuId === rowId && (
                              <div
                                className="table-list-2__context-menu"
                                ref={(el) => (menuRefs.current[rowId] = el)}
                              >
                                {options.map((opt, idx) => (
                                  <Link
                                    key={idx}
                                    to={opt.href || "#"}
                                    className="table-list-2__context-menu-option"
                                    onClick={opt.onClick}
                                  >
                                    {opt.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
<tr className="table-list-2__mobile-data-row" style={{ display: "none" }}>
  <td colSpan={headers.length + (contextualOptionsFn ? 1 : 0)}>
    <table className="table-list-2__mobile-data-table">
      <tbody>
        {headers.map((col, idx) => {
          const value = col.render ? col.render(item) : item[col.accessor];
          return (
            <tr key={idx}>
              <td>{col.label}</td>
              <td>{value}</td>
            </tr>
          );
        })}
      </tbody>
    </table>

<div className="table-list-2__mobile-data-options">
  {onPrimaryAction && (
    <button
      type="button"
      className="button--blue"
      onClick={() => onPrimaryAction(item)}
    >
      {getPrimaryActionLabel(item)}
    </button>
  )}

  {contextualOptionsFn && (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="slim-button more-options"
        onClick={(e) => toggleMenu(e, rowId)}
      >
        <ContextMenuIcon />
      </button>

      {openMenuId === rowId && (
        <div
          className="table-list__context-menu"
          ref={(el) => (menuRefs.current[rowId] = el)}
        >
          {contextualOptionsFn(item).map((opt, idx) => (
            <Link
              key={idx}
              to={opt.href || "#"}
              className="table-list__context-menu-option"
              onClick={(e) => {
                e.preventDefault();
                opt.onClick?.(e);
                setOpenMenuId(null); // Close after click
              }}
            >
              {opt.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )}
</div>



  </td>
</tr>




                    </React.Fragment>
                  );
                })
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default GroupedDataTable;
